import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { API_URL, tokenStore } from '../api/client';
import { useAuth } from './AuthContext';

const SocketContext = createContext(null);

/**
 * Keeps one socket open for the logged-in user. Screens subscribe to events
 * with useClinicEvent(), which is how a change at reception shows up on the
 * doctor's screen without a refresh.
 */
export function SocketProvider({ children }) {
  const { user } = useAuth();
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!user) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      setConnected(false);
      return undefined;
    }

    const socket = io(API_URL, {
      auth: { token: tokenStore.get() },
      transports: ['websocket', 'polling'],
    });

    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));
    socket.on('connect_error', () => setConnected(false));

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user]);

  const subscribe = useCallback((event, handler) => {
    const socket = socketRef.current;
    if (!socket) return () => {};
    socket.on(event, handler);
    return () => socket.off(event, handler);
  }, []);

  return (
    <SocketContext.Provider value={{ connected, subscribe }}>{children}</SocketContext.Provider>
  );
}

export function useSocket() {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useSocket must be used inside <SocketProvider>');
  return ctx;
}

/**
 * Runs `handler` whenever any of the given clinic events arrives. `events` may
 * be an inline array - it is keyed by name so it does not resubscribe on every
 * render, and the handler is read through a ref so it never needs memoising.
 */
export function useClinicEvent(events, handler) {
  const { subscribe, connected } = useSocket();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const key = Array.isArray(events) ? events.join('|') : events;

  useEffect(() => {
    const offs = key.split('|').map((e) => subscribe(e, (...args) => handlerRef.current(...args)));
    return () => offs.forEach((off) => off());
  }, [subscribe, connected, key]);
}
